import React, { memo } from "react";
import { Handle, Position } from "@xyflow/react";
import { compactCurrency } from "./costFormat";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro"; // <-- import styles to be used
import "./OrgNode.css";

// Word for a child node in the fold control — finance-friendly and dimension-neutral. Single source
// of truth, so it's trivial to swap (e.g. "department", "cost centre", "item").
const CHILD_UNIT = "area";

export default memo(({ id, data, isConnectable }) => {
	if (data.hidden) {
		return null;

	} else {
		const isRoot = id === "root";
		// Top-edge accent colour: the node's branch colour, or a calm graphite for the root trunk.
		const accent = isRoot ? "#334155" : data.branchColor;

		return (
			<>
				<Handle
					type="target"
					className="opacity-0"
					position={Position.Top}
					onConnect={(params) => console.log("handle onConnect", params)}
					isConnectable={isConnectable}
				/>
				<div
					style={{
						width: data.width,
						height: data.height,
						// The system catch-all gets a faint cool tint so it reads as a reserved, managed
						// bucket rather than a plain grey box.
						...(data.system && !isRoot ? { backgroundColor: "#f4f6fb" } : {}),
					}}
					className={`org-card org-card-anim border border-border rounded-lg transition-colors duration-200 relative group flex flex-col text-center ${
						(isRoot || data.system || !data.canEdit) ? "cursor-default" : "hover:cursor-grab active:cursor-grabbing"
					} ${
						(data.isHovered || data.selected) ? 'ring-2 ring-primary border-transparent' : ''
					} ${
						// Nodes with no allocated cost recede (lower elevation + slightly muted).
						!isRoot && !data.hasData ? 'org-card--flat opacity-80' : ''
					} ${
						isRoot ? "bg-card" : data.system ? "border-dashed" : "bg-card"
					}`}
				>
					{/* Branch accent: a thin colour bar along the top edge, shared across a top-level
					    branch's whole subtree, so sibling subtrees read as groups. The root (the trunk)
					    gets a calm graphite accent to set it apart as the total. */}
					{accent && (
						<div
							className="absolute top-0 left-0 right-0 h-[3px] rounded-t-lg"
							style={{ backgroundColor: accent }}
							aria-hidden="true"
						/>
					)}
					{/* System node tag (e.g. the catch-all) — read-only, platform-managed. */}
					{data.system && (
						<span className="absolute -top-[26px] left-1/2 -translate-x-1/2 z-10 inline-flex items-center h-5 px-2 rounded-full border border-border bg-surface-2 text-[10px] font-medium text-text-2 uppercase tracking-wide">
							System
						</span>
					)}
					{/* Draft diff badge — how this node differs from the active hierarchy (Draft
					    view only). Straddles the top edge, mirroring the collapse knob below. */}
					{data.change && data.change !== "unchanged" && (
						<span
							className={`absolute -top-[26px] left-1/2 -translate-x-1/2 z-10 inline-flex items-center h-5 px-2 rounded-full border text-[10px] font-medium ${
								data.change === "added"
									? "bg-success-soft text-success border-success"
									: data.change === "moved"
									? "bg-warning-soft text-warning border-warning"
									: "bg-primary-soft text-primary border-primary"
							}`}
						>
							{data.change === "added" ? "Added" : data.change === "moved" ? "Moved" : "Renamed"}
						</span>
					)}
					{/* Loading overlay */}
					{data.isLoading && (
						<div className="absolute inset-0 bg-card rounded-lg flex items-center justify-center z-10">
							<FontAwesomeIcon
								icon={icon({ name: "spinner", family: "sharp", style: "solid" })}
								className="text-text-3 text-2xl animate-spin"
							/>
						</div>
					)}
					{/* Drag handle (drag-to-reparent) — hover-only so the resting card stays clean.
					    Only when editing (Draft view); the Active hierarchy is read-only. All node
					    actions live in the right-hand inspector now, not on the card. */}
					{!isRoot && !data.system && data.canEdit && (
						<div className="absolute top-1 left-1 cursor-grab opacity-0 group-hover:opacity-100 transition-opacity">
							<FontAwesomeIcon
								title="Drag to move"
								icon={icon({ name: "grip-dots", family: "sharp", style: "light" })}
								className="rounded-md text-text-3 p-1 text-lg hover:text-text-2"
							/>
						</div>
					)}

					{/* Content */}
					{isRoot ? (
						<div className="flex-1 flex flex-col items-center justify-center px-4 min-h-0">
							{/* Root title/value match the branch cards' sizing (it stays the trunk via its frame and icon). */}
							<div className="text-base font-semibold leading-tight flex flex-row text-text items-center justify-center gap-2 max-w-full">
								<FontAwesomeIcon
									icon={icon({ name: "landmark", family: "classic", style: "light" })}
									className="text-text text-base"
								/>
								<span className="truncate" title={data.label}>{data.label}</span>
							</div>
							{data.hasData ? (
								<div className="mt-1.5 leading-tight">
									<div
										className={data.costMuted ? "text-base font-medium text-text-2 tabular-nums" : "text-lg font-bold text-text tabular-nums"}
										title={data.costExact}
									>
										{compactCurrency(data.subtree)}
										{data.costMuted && (
											<span className="ml-1 text-[10px] font-normal uppercase tracking-wide text-text-3 align-middle">active</span>
										)}
									</div>
								</div>
							) : (
								<div className="mt-1.5 text-base font-medium text-text-3 leading-none" title="No allocated cost in this period">—</div>
							)}
						</div>
					) : (
						<>
							<div className="flex-1 flex flex-col items-center justify-center px-4 min-h-0">
							{/* Node name. The internal code is intentionally not shown on the card (it's
							    noise for a finance user); it remains available in the Edit dialog. A long
							    name ellipsizes at the card's max width (full text in the tooltip). */}
							<div className="text-base font-semibold leading-tight text-text truncate px-1 pb-px max-w-full" title={data.label}>
								{data.label}
							</div>

							{/* Cost: subtree total is the headline (the node's accountability figure);
							    the muted line carries % of parent and the node's own (direct) cost.
							    A node with no allocation in the period shows "—", never a fake €0. */}
							{data.hasData ? (
								<div className="mt-0.5 leading-tight">
									<div
										className={data.costMuted ? "text-base font-medium text-text-2 tabular-nums" : "text-lg font-bold text-text tabular-nums"}
										title={data.costExact}
									>
										{compactCurrency(data.subtree)}
										{data.costMuted && (
											<span className="ml-1 text-[10px] font-normal uppercase tracking-wide text-text-3 align-middle">active</span>
										)}
									</div>
									{(data.pctOfParent != null || data.subtree !== data.direct) && (
										<div className="text-xs text-text-2">
											{data.pctOfParent != null ? `${data.pctOfParent}% of parent` : ""}
											{data.pctOfParent != null && data.subtree !== data.direct ? " · " : ""}
											{data.subtree !== data.direct ? `${compactCurrency(data.direct)} direct` : ""}
										</div>
									)}
								</div>
							) : (
								<div
									className="mt-1 text-base font-medium text-text-3 leading-none"
									title="No allocated cost in this period"
								>
									—
								</div>
							)}

							</div>

							{/* Fold footer — integrated at the bottom of the card with a hairline divider.
							    Plain language: collapsed "Show N areas ⌄", expanded "Hide ⌃". The whole
							    row is the hit target. `nodrag` so clicking it never starts a node drag. */}
							{data.hasChildren && (() => {
								const count = data.childCount || 0;
								const unit = count === 1 ? CHILD_UNIT : `${CHILD_UNIT}s`;
								return (
									<button
										type="button"
										aria-expanded={!data.collapsed}
										aria-label={data.collapsed ? `Show ${count} ${unit} under ${data.label}` : `Hide the ${unit} under ${data.label}`}
										title={data.collapsed ? `Show the ${count} ${unit} of ${data.label}` : "Hide"}
										onClick={(e) => { e.stopPropagation(); data.onToggleCollapse?.(id); }}
										className="nodrag cursor-pointer shrink-0 w-full h-8 inline-flex items-center justify-center gap-1.5 pb-0.5 whitespace-nowrap border-t border-card-bd text-text-2 hover:text-text hover:bg-surface-2 transition-colors rounded-b-lg"
									>
										<span className="text-xs font-medium leading-none">
											{data.collapsed ? <>Show <span className="tabular-nums">{count}</span> {unit}</> : "Hide"}
										</span>
										<FontAwesomeIcon
											icon={data.collapsed
												? icon({ name: "chevron-down", family: "sharp", style: "solid" })
												: icon({ name: "chevron-up", family: "sharp", style: "solid" })}
											className="text-[9px]"
										/>
									</button>
								);
							})()}
						</>
					)}

					{/* Cost-proportion footer: a fill bar flush to the bottom edge encoding this node's
					    share of its parent, in the branch colour — magnitude at a glance, not just a
					    number. Only for funded branch nodes (the root is the whole, empty nodes have
					    nothing to show). */}
					{!isRoot && data.hasData && data.pctOfParent != null && (
						<div className="absolute bottom-0 left-0 right-0 h-1.5 bg-black/5 rounded-b-lg overflow-hidden" aria-hidden="true">
							<div
								className="h-full"
								style={{
									width: `${Math.max(2, Math.min(100, data.pctOfParent))}%`,
									backgroundColor: data.branchColor || "#94a3b8",
								}}
							/>
						</div>
					)}
				</div>

				<Handle
					type="source"
					className="opacity-0"
					position={Position.Bottom}
					id="a"
					isConnectable={isConnectable}
				/>
			</>
		);
	}
});
